import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Undo2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { KIND_SHORT, describeParams, fmt, fmtTime } from "../preview/meta";

const PAGE = 50;
const STATUS = {
  running: "border-emerald-200 bg-emerald-50 text-emerald-700", done: "border-slate-200 bg-slate-50 text-slate-600",
  failed: "border-red-200 bg-red-50 text-red-700",
};

export function TypeBadge({ type }) {
  const undo = type === "undo";
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10px] font-semibold ${undo ? "border-violet-200 bg-violet-50 text-violet-700" : "border-sky-200 bg-sky-50 text-sky-700"}`}>
      {undo ? <Undo2 className="h-3 w-3" /> : <Send className="h-3 w-3" />}{undo ? "Undo" : "Apply"}</span>
  );
}

export const ResultCounts = ({ c = {} }) => (
  <span className="tabular-nums text-xs">
    <b className="text-emerald-700">{fmt(c.applied)}</b> applied · <b className="text-slate-600">{fmt(c.skipped)}</b> skipped · <b className={c.failed ? "text-red-700" : "text-slate-400"}>{fmt(c.failed)}</b> failed
  </span>
);

export const byKind = (bk = {}) => Object.entries(bk).map(([k, n]) => `${KIND_SHORT[k] || k} ${fmt(n)}`).join(" · ");

function Row({ a }) {
  return (
    <tr className="border-t border-slate-100 align-top transition-colors hover:bg-slate-50/70" data-testid={`bsh-row-${a.id}`}>
      <td className="px-4 py-2.5 text-xs text-slate-700">{fmtTime(a.started_at)}<div className="font-mono text-[10px] text-slate-400">{a.id}</div></td>
      <td className="px-3 py-2.5"><TypeBadge type={a.type} /></td>
      <td className="px-3 py-2.5 text-xs text-slate-700">{a.started_by_name || "—"}</td>
      <td className="px-3 py-2.5 text-xs text-slate-600">{byKind(a.by_kind)}
        <div className="text-[10px] text-slate-400">{a.type === "undo" ? `of ${(a.of_applies || []).join(", ")}` : a.run_params ? describeParams(a.run_params) : a.run_id}</div></td>
      <td className="px-3 py-2.5"><ResultCounts c={a.counts} />{a.note && <div className="text-[10px] text-slate-400">{a.note}</div>}</td>
      <td className="px-3 py-2.5"><span className={`rounded border px-1.5 py-px text-[10px] font-semibold ${STATUS[a.status] || STATUS.done}`}>{a.status === "running" ? `running ${a.done}/${a.total}` : a.status}</span>
        {a.email && <div className="mt-1 text-[10px] text-slate-400">e-mail {a.email.status}</div>}</td>
      <td className="px-4 py-2.5 text-right"><Link to={`/billing-sync/apply-history/${a.id}`} className="text-xs font-medium text-emerald-700 hover:underline" data-testid={`bsh-open-${a.id}`}>Rows →</Link></td>
    </tr>
  );
}

export function ApplyHistory() {
  const [type, setType] = usePersistedState("apply-history.type", "");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/billing-sync/applies", { params: { type, skip, limit: PAGE } }).then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load the apply history"));
  }, [type, skip]);
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="bsh-list">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <h3 className="font-heading text-sm font-semibold text-slate-800">Apply History</h3>
        {data && <span className="text-xs text-slate-500" data-testid="bsh-total">{fmt(data.total)} jobs</span>}
        <div className="ml-auto flex gap-1">{[["", "All"], ["apply", "Applies"], ["undo", "Undos"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setSkip(0); setType(k); }} data-testid={`bsh-type-${k || "all"}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${type === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>))}</div>
      </div>
      {!data ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-300" /> : !data.applies.length ? (
        <p className="p-10 text-center text-sm text-slate-500" data-testid="bsh-empty">Nothing applied yet. Tick rows in the Tracked Items Preview and press Apply.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>
            <th className="px-4 py-2.5 text-left">When</th><th className="px-3 py-2.5 text-left">Type</th><th className="px-3 py-2.5 text-left">Who</th>
            <th className="px-3 py-2.5 text-left">What changed</th><th className="px-3 py-2.5 text-left">Result</th><th className="px-3 py-2.5 text-left">Status</th><th /></tr></thead>
          <tbody>{data.applies.map((a) => <Row key={a.id} a={a} />)}</tbody>
        </table>)}
      {data && data.total > PAGE && (
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          {fmt(skip + 1)}–{fmt(Math.min(skip + PAGE, data.total))} of {fmt(data.total)}
          <Button size="sm" variant="ghost" disabled={!skip} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="bsh-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} data-testid="bsh-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>)}
    </Card>
  );
}
